import { useState } from 'react';
import { useSubscribeToPushMutation } from 'app/api/pushApiSlice';
import MLToast from 'components/common/Toast/Toast';

// PushManager.subscribe needs the VAPID public key as a Uint8Array, not the
// base64url string it's distributed as.
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
};

const isSupported = () =>
  'serviceWorker' in navigator && 'PushManager' in window;

const usePushNotifications = () => {
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  const [subscribeToPush] = useSubscribeToPushMutation();
  const { showToast } = MLToast();

  const subscribe = async () => {
    if (!isSupported()) {
      setPermission('unsupported');
      showToast('This browser does not support push notifications.', 'error');
      return;
    }

    // If a previous visit already denied the prompt, Chromium browsers won't
    // show it again — requestPermission() just resolves 'denied' silently.
    // Surface that instead of failing silently.
    if (Notification.permission === 'denied') {
      showToast(
        'Notifications are blocked for this site. Allow them in your browser\'s site settings, then try again.',
        'error',
        6000
      );
      return;
    }

    const result = await Notification.requestPermission();
    setPermission(result);
    if (result !== 'granted') {
      showToast('Notifications were not enabled.', 'warning');
      return;
    }

    if (!process.env.REACT_APP_VAPID_PUBLIC_KEY) {
      console.error(
        'REACT_APP_VAPID_PUBLIC_KEY is not set — restart the dev server after adding it to .env.'
      );
      showToast('Could not enable notifications right now.', 'error');
      return;
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(
        process.env.REACT_APP_VAPID_PUBLIC_KEY
      ),
    });

    await subscribeToPush(subscription.toJSON());
    showToast('Checkout reminders enabled for this browser.', 'success');
  };

  return { permission, subscribe };
};

export default usePushNotifications;
